import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Assessment, AssessmentItem } from '@/models/Assessment';

type UseAssessmentGradeValidateProps = {
  assessment: Assessment;
  items: AssessmentItem[];
  currentItemId?: number;
};

export type ValidateMaxGradeFn = (value: number | undefined) => true | string;

const useAssessmentGradeValidate = ({
  assessment,
  items,
  currentItemId,
}: UseAssessmentGradeValidateProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const otherItemsSum = items.reduce(
    (sum, item) => (item.id === currentItemId ? sum : sum + item.maxGrade),
    0,
  );

  const available = Math.max(assessment.maxGrade - otherItemsSum, 0);

  const validateMaxGrade = useCallback<ValidateMaxGradeFn>(
    value => {
      if (value === undefined || Number.isNaN(value)) return true;

      if (otherItemsSum + value > assessment.maxGrade) {
        return t('assessmentItemMaxGradeExceedsAssessment', {
          assessmentMaxGrade: assessment.maxGrade,
        });
      }

      return true;
    },
    [assessment.maxGrade, otherItemsSum, t],
  );

  return { validateMaxGrade, available };
};

export default useAssessmentGradeValidate;
